<template>
  <Modal
    v-if="role && shown"
    class="organisation-member-add-dialog text-left"
    v-model="shown"
    :title="`Add ${role}s to this team`"
    data-test="nmr-modal"
  >
    <SearchUsers
      :errors="errors"
      :existingMembers="memberList"
      @select-user="selectUser"
      @update-search-state="updateSearchState"
    >
      <div class="col-sm-12">
        <p data-test="nmr-message">Add {{ role }}s who do not belong to this team.</p>
      </div>
    </SearchUsers>
    <template #footer>
      <div>
        <span
          v-if="addedUsers.length > 0"
          class="text-success text-big"
          style="margin-right: 10px"
          data-test="nmr-added-users"
        >
          <span class="glyphicon glyphicon-ok"></span> Added
          {{ addedUsers.length }}
          {{ addedUsers.length > 1 ? role + "s" : role }}
        </span>
        <button
          class="btn btn-primary"
          @click="assignUserToRole()"
          :disabled="!selectedUser || isLoading || isSaving"
          data-test="nmr-add-btn"
          type="button"
        >
          {{ isSaving ? "Saving..." : "Add" }}
        </button>

        <button class="btn btn-default" @click="close()" data-test="nmr-close-btn" type="button">
          Close
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { Modal } from "uiv";
import { computed, ref } from "vue";
import SearchUsers from "./search-users.vue";
import { GroupUserRoles } from "./group-user-roles.enum";
import {
  GroupUserRoleApiModel,
  ShortUserDetailsApiModel,
  TeamCurrentMemberApiModel,
} from "../../../api-models";
import { useAxios } from "../../../composables/api";

const props = defineProps<{
  role: GroupUserRoles;
  modelValue: boolean;
  existingMembers: TeamCurrentMemberApiModel[];
  teamId: string;
}>();

const emit = defineEmits(["update:modelValue"]);

const selectedUser = ref<ShortUserDetailsApiModel | null>(null);

const addedUsers = ref<ShortUserDetailsApiModel[]>([]);

const isLoading = ref(false);

const isSaving = ref(false);

const errors = ref<string[]>([]);

const shown = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const memberList = computed<ShortUserDetailsApiModel[]>(() => {
  return props.existingMembers.map((m) => {
    return m.userDetails;
  });
});

const selectUser = (selection: ShortUserDetailsApiModel): void => {
  selectedUser.value = selection;
};

const assignUserToRole = (): void => {
  if (selectedUser.value) {
    isSaving.value = true;
    const userFullName = `${selectedUser.value.firstName} ${selectedUser.value.lastName}`;
    const editUserRole: GroupUserRoleApiModel = {
      id: selectedUser.value.id,
      groupId: props.teamId,
      name: userFullName,
      role: props.role.toLowerCase(),
    };
    const { errorStatus } = useAxios("/api/admin/teamrole", {
      isFatal: false,
      httpMethod: "POST",
      data: editUserRole,
    });
    if (errorStatus.value) {
      errors.value = ["There was a problem assigning this user, please try again."];
    } else if (selectedUser.value) {
      addedUsers.value.push(selectedUser.value);
    }
    isSaving.value = false;
    selectedUser.value = null;
  }
};

const close = (): void => {
  let isRefreshNeeded = false;
  if (addedUsers.value.length) {
    isRefreshNeeded = true;
  }
  emit("update:modelValue", isRefreshNeeded);
  selectedUser.value = null;
  addedUsers.value = [];
};

const updateSearchState = (isSearching: boolean): void => {
  isLoading.value = isSearching;
};
</script>
